/**
 * MEU ESTOQUE (antiga «catira»; o nome de código e de banco ficou) — o
 * estoque do juntador de gado: quantas cabeças tem, quanto custou cada uma
 * (custo médio), quanto ganhou nas vendas e o que a contagem no pasto achou de
 * diferença. Sem identificação de animal: cabeças e dinheiro.
 *
 * R2D · POR CATEGORIA: cada categoria (bezerro, vaca…) tem as suas cabeças, o
 * seu custo médio e o seu resultado; a venda de bezerros sai pelo custo médio
 * dos bezerros. Quem trabalha com uma categoria só continua vendo um estoque
 * só (as contas: src/dominio/catira.ts).
 *
 * Quem vê o quê (o banco confere de novo, migrações 008 e 015):
 *  - gestor: vê e lança (recria, morte, contagem, e as fichas que entram e saem);
 *  - consulta: vê tudo, sem nenhum botão de gravar;
 *  - comprador: não vê o estoque (a entrada nem aparece para ele).
 *
 * QUANDO A CONTA NÃO BATE, O APP ORIENTA E NUNCA BLOQUEIA: a venda maior que o
 * estoque propõe «cabeças encontradas»; a contagem diferente propõe «sumidos»
 * ou «encontradas»; a contagem zero propõe a zeragem. Tudo com data, e o
 * ajuste vai para o RESULTADO (ajuste de contagem), nunca para o caixa.
 */
import { useMemo, useState } from 'react'
import { baixar } from '../dados/arquivos'
import { lerEstado, usarEstado } from '../dados/armazem'
import {
  abasDaCatira, alvoPorCategoria, anularMovimento, CATEGORIA_GERAL, CATEGORIAS_DE_RECRIA, categoriaDoMovimento, categoriaNoEstoque,
  categoriasComAlgo, diferencasPorCategoria, ESTOQUE_PRINCIPAL, estoqueAntes, jaLancadoDaFicha, jaLancadoPorCategoria, juntarPorCategoria,
  lancarMovimentos, lerCatira, NOME_DO_TIPO, pesosPorCategoria, RATEIO, recalcular, resultadosDoPeriodo, usarCatira,
  type DiferencaDaCategoria, type Efeito, type Movimento, type ParteDaCategoria, type SaldoDaCategoria,
} from '../dados/catira'
import { lerPagamentos, usarPagamentos, type EstadoDePagamentos } from '../dados/pagamentos'
import { lerSessao, usarSessao, type Eu } from '../dados/sessao'
import { agora, dataBR, hoje, kg as kgTexto, lerNumero, novoId, num, quando, reais } from '../dominio/numeros'
import { montarXlsx } from '../dominio/planilha'
import { porteirasDa, temPorteiraDeEstoque } from '../dominio/destinos'
import { ferramentaLigada } from '../dominio/ferramentas'
import type { Ficha } from '../dominio/tipos'
import { pode } from '../dominio/permissoes'
import { avisar, Campo, Dica, Escolha, Janela, pedirMotivo, Tarja, type Ir } from '../ui'
import { NotasLigadas } from './Notas'

/* ---------------------------------------------------------------------------
 * Datas do filtro
 * ------------------------------------------------------------------------- */

const f2 = (n: number) => String(n).padStart(2, '0')
const iso = (d: Date) => `${d.getFullYear()}-${f2(d.getMonth() + 1)}-${f2(d.getDate())}`

/** primeiro e último dia do mês de uma data, com deslocamento de meses */
function mes(base: string, deslocar = 0): [string, string] {
  const [a, m] = base.split('-').map(Number) as [number, number]
  return [iso(new Date(a, m - 1 + deslocar, 1)), iso(new Date(a, m + deslocar, 0))]
}

/* ---------------------------------------------------------------------------
 * Lançar — funções usadas pela tela do estoque e pela ficha
 * ------------------------------------------------------------------------- */

/** Um movimento novo, no nome de quem lança, com a hora do aparelho. */
function novo(eu: Eu, m: Pick<Movimento, 'tipo' | 'data'> & Partial<Movimento>): Movimento {
  return {
    id: novoId(), estoqueId: ESTOQUE_PRINCIPAL, em: agora(), cabecas: 0, contadas: null, valor: 0, categoria: '',
    descricao: '', fichaId: null, lancadoPor: eu.pessoa.id, lancadoPorNome: eu.pessoa.nome, lancadoEm: agora(),
    anulacao: null, categoriaGado: CATEGORIA_GERAL, ...m,
  }
}

/** Um milésimo antes: a contagem das «encontradas» fica logo antes da saída que ela corrige. */
function umPoucoAntes(em: string): string {
  return new Date(Date.parse(em) - 1).toISOString()
}

const cab = (n: number) => `${num(n)} ${Math.abs(n) === 1 ? 'cabeça' : 'cabeças'}`
/** R2D · «R$ 0,00» quando não há nada — nunca «R$» vazio nem «−R$ 0,00» */
const dinheiro = (n: number | null | undefined) => reais(n ? n : 0)

/** O saldo de cada categoria no estoque logo antes de um momento. */
function saldosAntes(data: string, em: string): SaldoDaCategoria[] {
  const movs = lerCatira().movimentos
  return recalcular(movs.filter((m) => m.data < data || (m.data === data && m.em < em))).categorias
}

/**
 * Antes de tirar cabeças (venda, morte, ajuste): confere o estoque de cada
 * categoria naquele dia. Se não há cabeças bastantes, PROPÕE registrar as
 * encontradas — nunca trava. Devolve os movimentos de contagem a lançar junto
 * (vazio se não precisa), ou `false` se a pessoa desistiu. `automatico`: sem
 * pergunta — o recálculo conta a falta como encontradas e a tela avisa.
 */
function conferirSaidas(eu: Eu, saidas: { categoria: string; sai: number }[], data: string, em: string, oque: string,
  automatico = false): Movimento[] | false {
  const faltas = saidas.map((s) => ({ ...s, falta: s.sai - estoqueAntes(lerCatira().movimentos, data, em, ESTOQUE_PRINCIPAL, s.categoria).cabecas }))
    .filter((s) => s.falta > 0)
  if (!faltas.length || automatico) return []
  const texto = faltas.map((s) => `• ${s.categoria}: faltam ${cab(s.falta)}`).join('\n')
  if (!window.confirm(`O estoque ficaria negativo — o rebanho era maior que o registrado:\n${texto}\n\n`
    + `Registrar as cabeças encontradas e seguir com ${oque}?\n\n`
    + `As encontradas entram pelo custo médio da categoria, numa linha própria («ajuste de contagem»), fora do lucro. `
    + `Se houve sumidos antes, elas os anulam primeiro.`)) return false
  return faltas.map((s) => novo(eu, {
    tipo: 'contagem', data, em: umPoucoAntes(em), contadas: s.sai, cabecas: s.falta, categoriaGado: s.categoria,
    descricao: `Cabeças encontradas (${oque})`,
  }))
}

/** A comissão da ficha, como o fechamento (2E) a enxerga. */
function comissaoDa(pg: EstadoDePagamentos, fichaId: string) {
  return pg.comissoes[fichaId] ?? null
}

/** «Bezerro: 20 cab · R$ 38.000,00» — uma linha por categoria, para as confirmações. */
function linhasDasPartes(partes: ParteDaCategoria[]): string {
  return partes.map((p) => `• ${p.categoria}: ${cab(p.cabecas)} · ${reais(p.valor)}`).join('\n')
}

/**
 * Põe uma ficha encerrada no estoque, categoria por categoria (conforme os
 * lotes): Compra entra (cabeças e custo total com comissão, repartido ao
 * centavo); Venda sai (cabeças e líquido do romaneio). Só o gestor chega aqui.
 * `automatico` (Venda do meu estoque, ao encerrar): sem perguntas.
 */
export function lancarFicha(f: Ficha, eu: Eu, pg: EstadoDePagamentos, automatico = false): boolean {
  if (f.situacao !== 'encerrada') { window.alert('Encerre a ficha primeiro: o estoque recebe as cabeças e os valores do romaneio.'); return false }
  const alvo = alvoPorCategoria(f, comissaoDa(pg, f.id))
  if (!alvo) return false
  if (jaLancadoDaFicha(lerCatira().movimentos, f.id)) { if (!automatico) avisar('Esta ficha já está no Meu estoque'); return false }
  // 3B: com porteiras, só a «Meu estoque» entra; cliente e revenda não são estoque
  const apartada = alvo.tipo === 'entrada' && porteirasDa(f).length > 0
  const total = alvo.partes.reduce((s, p) => s + p.cabecas, 0)
  const valor = alvo.partes.reduce((s, p) => s + Math.round(p.valor * 100), 0) / 100
  if (total <= 0) {
    if (!automatico) window.alert(apartada ? 'Nenhum animal na porteira «Meu estoque» desta ficha. Cliente e revenda não entram no estoque.' : 'O romaneio desta ficha não tem cabeças.')
    return false
  }
  const em = agora()
  const descricao = `Ficha ${f.numero} · ${f.produtor.nome}`
  if (alvo.tipo === 'entrada') {
    if (!automatico && !window.confirm(`Entrar no estoque: ${cab(total)}, custo total ${reais(valor)} (${reais(valor / total)} por cabeça).\n\n`
      + (alvo.partes.length > 1 ? `Separado por categoria, conforme os lotes:\n${linhasDasPartes(alvo.partes)}\n\n` : '')
      + (alvo.comissaoPendente
        ? 'A comissão ainda não foi lançada e entra depois: quando você lançar, a diferença vira um ajuste de compra, com data.'
        : apartada
          ? 'É o custo da porteira «Meu estoque» na aba Custo: a parte do vendedor, a comissão a pagar, o frete e os gastos dela — sem comissão de cliente.'
          : 'É o custo total da aba Custo: vendedor, comissão, frete e gastos.'))) return false
    lancarMovimentos(...alvo.partes.filter((p) => p.cabecas > 0).map((p) => novo(eu, {
      tipo: 'entrada', data: f.data, em, cabecas: p.cabecas, valor: p.valor, fichaId: f.id, descricao,
      categoriaGado: p.categoria, kg: p.kg, arrobas: p.arrobas,
    })))
    avisar('Entrou no estoque — vai no próximo Sincronizar')
    return true
  }
  // a venda sai das categorias DO ESTOQUE (o lote «Boi gordo» de quem só tem «Geral» sai do «Geral»)
  const saldos = saldosAntes(f.data, em)
  const partes = juntarPorCategoria(alvo.partes.map((p) => ({ ...p, categoria: categoriaNoEstoque(p.categoria, saldos) }))).filter((p) => p.cabecas > 0)
  const encontradas = conferirSaidas(eu, partes.map((p) => ({ categoria: p.categoria, sai: p.cabecas })), f.data, em, 'a venda', automatico)
  if (encontradas === false) return false
  if (!encontradas.length && !automatico) {
    const lucro = partes.map((p) => {
      const s = saldos.find((x) => x.categoria === p.categoria)
      return s?.custoMedio === null || !s ? null : p.valor - s.custoMedio * p.cabecas
    })
    const conhecido = lucro.every((x) => x !== null)
    if (!window.confirm(`Sair do estoque: ${cab(total)} vendidas por ${reais(valor)} (líquido do romaneio).`
      + (partes.length > 1 || partes[0]?.categoria !== CATEGORIA_GERAL ? `\n\n${linhasDasPartes(partes)}` : '')
      + (conhecido ? `\n\nPelo custo médio de cada categoria → lucro de cerca de ${reais(lucro.reduce((s: number, x) => s + (x ?? 0), 0))}.` : ''))) return false
  }
  lancarMovimentos(...encontradas, ...partes.map((p) => novo(eu, {
    tipo: 'venda', data: f.data, em, cabecas: -p.cabecas, valor: p.valor, fichaId: f.id, descricao,
    categoriaGado: p.categoria, kg: p.kg, arrobas: p.arrobas,
  })))
  avisar('Saiu do estoque — vai no próximo Sincronizar')
  return true
}

/**
 * A ficha mudou depois de entrar (ou sair): lança as diferenças como ajustes,
 * categoria por categoria, com a data de hoje. O passado não muda em silêncio.
 */
function lancarDiferencas(difs: DiferencaDaCategoria[], fichas: Ficha[], eu: Eu, automatico = false): void {
  if (!automatico) {
    const linhas = difs.map((d) => {
      const f = fichas.find((x) => x.id === d.fichaId)
      return `• Ficha ${f?.numero ?? '?'} · ${d.categoria}: ${d.cabecas ? `${d.cabecas > 0 ? '+' : ''}${num(d.cabecas)} cab · ` : ''}${d.valor >= 0 ? '+' : '−'} ${reais(Math.abs(d.valor))}`
    })
    if (!window.confirm(`Lançar ${difs.length === 1 ? 'esta diferença' : 'estas diferenças'} como ajuste, com a data de hoje?\n\n${linhas.join('\n')}\n\nO que já foi lançado antes não muda.`)) return
  }
  const data = hoje()
  const movs: Movimento[] = []
  const porFicha = new Map<string, DiferencaDaCategoria[]>()
  for (const d of difs) porFicha.set(d.fichaId, [...(porFicha.get(d.fichaId) ?? []), d])
  for (const [fichaId, ds] of porFicha) {
    const f = fichas.find((x) => x.id === fichaId)
    const em = agora()
    const descricao = `Ficha ${f?.numero ?? ''}${f?.situacao === 'cancelada' ? ' (cancelada)' : ''} · ${f?.produtor.nome ?? ''}`
    // cabeças que SAEM do estoque com este ajuste: compra com menos cabeças, ou venda com mais
    const saidas = ds.map((d) => ({ categoria: d.categoria, sai: d.tipo === 'entrada' ? -d.cabecas : d.cabecas })).filter((s) => s.sai > 0)
    const enc = saidas.length ? conferirSaidas(eu, saidas, data, em, `o ajuste da ficha ${f?.numero ?? ''}`, automatico) : []
    if (enc === false) continue
    movs.push(...enc)
    for (const d of ds) {
      movs.push(novo(eu, {
        tipo: d.tipo === 'entrada' ? 'ajuste_entrada' : 'ajuste_venda', data, em,
        cabecas: d.tipo === 'entrada' ? d.cabecas : -d.cabecas, valor: d.valor, fichaId: d.fichaId, descricao,
        categoriaGado: d.categoria, kg: d.kg, arrobas: d.arrobas,
      }))
    }
  }
  if (!movs.length) return
  lancarMovimentos(...movs)
  avisar('Ajustes lançados — vão no próximo Sincronizar')
}

/** Tira a ficha do estoque: anula a entrada (ou venda) e os ajustes dela, de todas as categorias, com o mesmo motivo. */
function tirarFicha(fichaId: string, numero: string, eu: Eu): void {
  const ja = jaLancadoDaFicha(lerCatira().movimentos, fichaId)
  if (!ja) return
  const motivo = pedirMotivo(`Tirar a ficha ${numero} do Meu estoque?\n\nA ${ja.tipo === 'entrada' ? 'entrada' : 'venda'} e os ajustes dela continuam na lista, riscados, com o motivo, e o estoque é recalculado. Qual o motivo?`, true)
  if (motivo === null) return
  for (const m of lerCatira().movimentos.filter((x) => x.fichaId === fichaId && !x.anulacao && !x.recusa)) anularMovimento(m.id, motivo, eu.pessoa.id)
  avisar('Tirada do Meu estoque — vai no próximo Sincronizar')
}

/**
 * R2D · «Venda do meu estoque»: ao encerrar a ficha (Pesagem ou Romaneio),
 * a venda sai SOZINHA do estoque, categoria por categoria — o gestor não marca
 * nada depois. Reencerrada com mudança, lança a diferença sozinha também.
 * Só o gestor, só com a ferramenta ligada; ficha que o gestor TIROU do
 * estoque (movimento anulado) não volta sozinha.
 */
export function saidaAutomaticaDoEstoque(fichaId: string): void {
  const s = lerSessao()
  if (s.tipo !== 'dentro' || !pode(s.eu.pessoa.papel, 'catira') || !ferramentaLigada(s.eu.conta, 'catira')) return
  const f = lerEstado().fichas.find((x) => x.id === fichaId)
  if (!f || f.finalidade !== 'Venda' || !f.vendaDoEstoque || f.situacao !== 'encerrada') return
  const movs = lerCatira().movimentos
  if (jaLancadoDaFicha(movs, f.id)) {
    const difs = diferencasPorCategoria(movs, f, alvoPorCategoria(f, null))
    if (difs.length) lancarDiferencas(difs, [f], s.eu, true)
    return
  }
  if (movs.some((m) => m.fichaId === f.id && !m.recusa)) return // tirada do estoque pelo gestor: não volta sozinha
  lancarFicha(f, s.eu, lerPagamentos(), true)
}

/* ---------------------------------------------------------------------------
 * A tela
 * ------------------------------------------------------------------------- */

export function Catira({ eu, ir, fichaId }: { eu: Eu; ir: Ir; fichaId?: string }) {
  const cat = usarCatira()
  const { fichas } = usarEstado()
  const pg = usarPagamentos()
  const papel = eu.pessoa.papel
  // 3E: quem lança é quem a matriz deixa (o gestor); a Consulta só vê
  const gestor = pode(papel, 'catira')
  const [periodo, setPeriodo] = useState<[string, string]>(() => mes(hoje()))
  const [de, ate] = periodo
  const [form, setForm] = useState<'recria' | 'morte' | 'contagem' | null>(null)
  const [verFora, setVerFora] = useState(false)
  const [verAjuste, setVerAjuste] = useState(false)

  const calc = useMemo(() => recalcular(cat.movimentos), [cat.movimentos])
  const res = resultadosDoPeriodo(calc, de, ate)
  const efeitoDe = useMemo(() => new Map(calc.efeitos.map((e) => [e.m.id, e])), [calc])
  const pendentes = new Set(cat.pendentes)
  const fichaDe = (id: string | null) => (id ? fichas.find((f) => f.id === id) : undefined)
  const cats = categoriasComAlgo(calc)
  // uma categoria só (ou nenhuma): a tela de sempre, um estoque só
  const variasCategorias = cats.length > 1
  const pesos = pesosPorCategoria(calc, ate).filter((p) => p.pesoEntrada !== null || p.custoPorArrobaEntrada !== null || p.pesoVenda !== null)

  // fichas no estoque que mudaram depois (comissão lançada, pesou mais, cancelou)
  const diferencas = useMemo(() => {
    const ids = new Set(cat.movimentos.filter((m) => m.fichaId).map((m) => m.fichaId!))
    const out: DiferencaDaCategoria[] = []
    for (const id of ids) {
      const f = fichas.find((x) => x.id === id)
      if (!f) continue
      out.push(...diferencasPorCategoria(cat.movimentos, f, alvoPorCategoria(f, comissaoDa(pg, f.id))))
    }
    return out
  }, [cat.movimentos, fichas, pg])
  const fichasMudadas = new Set(diferencas.map((d) => d.fichaId)).size

  // fichas encerradas que ainda não estão no estoque (lembrete, nunca obrigação)
  const naCatira = new Set(cat.movimentos.filter((m) => m.fichaId && !m.anulacao && !m.recusa).map((m) => m.fichaId!))
  const vendasFora = fichas.filter((f) => f.finalidade === 'Venda' && f.situacao === 'encerrada' && !naCatira.has(f.id))
  // só compra que PODE entrar no estoque: com porteira «Meu estoque», ou sem
  // porteira nenhuma. A de intermediação/revenda pura vai inteira para outro
  // (simulação de 06/10/2026: 6 compras de intermediação apareciam como «fora»)
  const comprasFora = fichas.filter((f) => f.finalidade === 'Compra' && f.situacao === 'encerrada' && !naCatira.has(f.id) && temPorteiraDeEstoque(f))
  const furos = calc.efeitos.filter((e) => e.faltaram > 0)

  if (!pode(papel, 'verCatira')) return <div className="cartao vazio">O Meu estoque é só do gestor.</div>

  const doPeriodo = calc.efeitos.filter((e) => e.m.data >= de && e.m.data <= ate)
  // os anulados e recusados do período também aparecem (riscados): nada some
  const foraDaConta = cat.movimentos.filter((m) => (m.anulacao || m.recusa) && m.data >= de && m.data <= ate)
  // mais novo primeiro, na ordem inversa do recálculo (no mesmo dia e hora, o último que contou vem em cima)
  const ordem = new Map(calc.efeitos.map((e, i) => [e.m.id, i]))
  const lista = [...doPeriodo.map((e) => ({ m: e.m, e })), ...foraDaConta.map((m) => ({ m, e: undefined as Efeito | undefined }))]
    .sort((a, b) => b.m.data.localeCompare(a.m.data) || b.m.em.localeCompare(a.m.em) || (ordem.get(b.m.id) ?? -1) - (ordem.get(a.m.id) ?? -1))

  const daFicha = fichaId ? fichaDe(fichaId) : undefined

  function exportar() {
    baixar(montarXlsx(abasDaCatira(calc, de, ate, (id) => fichaDe(id)?.numero ?? id)), `meu-estoque-${de}_a_${ate}.xlsx`,
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    avisar('Planilha salva em Downloads')
  }

  function anular(m: Movimento) {
    if (m.tipo === 'entrada' || m.tipo === 'venda') { tirarFicha(m.fichaId!, fichaDe(m.fichaId)?.numero ?? '', eu); return }
    const motivo = pedirMotivo(`Anular ${NOME_DO_TIPO[m.tipo].toLowerCase()} de ${dataBR(m.data)}?\n\nO movimento continua na lista, riscado, com o motivo, e o estoque é recalculado. Qual o motivo?`, true)
    if (motivo === null) return
    anularMovimento(m.id, motivo, eu.pessoa.id)
    avisar('Anulado — vai no próximo Sincronizar')
  }

  return (
    <div>
      <h2 className="secao">Meu estoque — gado no pasto</h2>
      <Dica>Cabeças e dinheiro, sem identificar animal, separados por categoria. Entra a compra que você marca, sai a venda; a recria
        soma no custo; a contagem no pasto acerta a diferença. {gestor ? '' : 'Você só vê: quem lança é o gestor.'}</Dica>

      <div className="resultado">
        <div className="linha" style={{ textAlign: 'center' }}>
          <div><div style={{ fontSize: '2.4rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{num(calc.cabecas)}</div>
            <div className="mini-dica">cabeças no estoque</div></div>
          <div><div style={{ fontSize: '1.7rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', paddingTop: 8 }}>{dinheiro(calc.custoMedio)}</div>
            <div className="mini-dica">custo médio por cabeça{variasCategorias ? ' (todas)' : ''}</div></div>
        </div>
        <div className="tl total"><span>Custo total do estoque</span><b>{dinheiro(calc.custo)}</b></div>
        {!variasCategorias && cats[0] && cats[0].categoria !== CATEGORIA_GERAL && <div className="mini-dica">Categoria: {cats[0].categoria}</div>}
        {calc.semGado > 0 && <div className="mini-dica">{dinheiro(calc.semGado)} de recria esperando gado: entra no custo da próxima compra.</div>}
        {cats.filter((x) => x.cabecas === 0 && x.custo > 0).map((x) => (
          <div className="mini-dica" key={x.categoria}>{variasCategorias ? `${x.categoria}: ` : ''}custo de {dinheiro(x.custo)} esperando gado (recria lançada antes
            da compra): entra no custo da próxima compra {variasCategorias ? 'desta categoria' : ''}.</div>
        ))}
      </div>

      {variasCategorias && (
        <div className="cartao">
          <div className="mini-dica" style={{ marginTop: 0 }}><b>Por categoria</b> — cada uma com o seu custo médio</div>
          {cats.map((x) => (
            <div className="tl" key={x.categoria}>
              <span>{x.categoria} · {cab(x.cabecas)}{x.sumidos ? ` · ${num(x.sumidos)} sumido(s)` : ''}</span>
              <b>{dinheiro(x.custoMedio)}/cab · {dinheiro(x.custo)}</b>
            </div>
          ))}
        </div>
      )}

      {daFicha && <BlocoDaFicha f={daFicha} eu={eu} pg={pg} movs={cat.movimentos} efeitoDe={efeitoDe} pendentes={pendentes} ir={ir} />}

      {diferencas.length > 0 && (
        <Tarja>
          {fichasMudadas === 1 ? 'Uma ficha do Meu estoque mudou' : `${fichasMudadas} fichas do Meu estoque mudaram`} depois de lançada (comissão lançada,
          gasto novo, pesou mais ou cancelou). {gestor ? 'O estoque só muda quando você lançar a diferença.' : 'O gestor lança a diferença.'}
          {gestor && <button className="botao" style={{ marginTop: 8 }} onClick={() => lancarDiferencas(diferencas, fichas, eu)}>Lançar as diferenças</button>}
        </Tarja>
      )}
      {furos.length > 0 && (
        <Tarja tipo="erro">
          A conta não bateu: {furos.map((e) => `${NOME_DO_TIPO[e.m.tipo].toLowerCase()} de ${dataBR(e.m.data)}${variasCategorias || e.categoria !== CATEGORIA_GERAL ? ` (${e.categoria})` : ''} tirou ${cab(e.faltaram)} a mais do que havia`).join('; ')}.
          O app contou essas cabeças como encontradas para o estoque não ficar negativo. {gestor ? 'Faça uma «Contagem no pasto» para acertar.' : ''}
        </Tarja>
      )}
      {(vendasFora.length > 0 || comprasFora.length > 0) && (
        <div className="cartao">
          <div className="tl clicavel" onClick={() => setVerFora(!verFora)}>
            <span>Fichas encerradas fora do Meu estoque: {vendasFora.length} venda(s), {comprasFora.length} compra(s)</span><b>{verFora ? '▲' : '▼'}</b>
          </div>
          {verFora && (
            <>
              <div className="mini-dica">Compra para cliente (intermediação) não é estoque: fica fora. Ponha aqui só o que é seu.</div>
              {[...vendasFora, ...comprasFora].sort((a, b) => b.data.localeCompare(a.data)).map((f) => (
                <div className="tl" key={f.id}>
                  <span className="clicavel" onClick={() => ir({ tela: 'ficha', id: f.id, aba: 'romaneio' })}>
                    {f.finalidade} · Nº {f.numero} · {dataBR(f.data)} · {f.produtor.nome}</span>
                  {gestor && <button className="mini" onClick={() => lancarFicha(f, eu, pg)}>{f.finalidade === 'Venda' ? 'Sair do estoque' : 'Entrar no estoque'}</button>}
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {gestor && (
        <div className="linha" style={{ margin: '8px 0 4px' }}>
          <button className="botao" onClick={() => setForm('recria')}>+ Recria</button>
          <button className="botao fantasma" onClick={() => setForm('morte')}>Morte</button>
        </div>
      )}
      {gestor && <button className="botao fantasma" onClick={() => setForm('contagem')}>Contagem no pasto</button>}

      <h2 className="secao">Período</h2>
      <div className="cartao">
        <div className="linha">
          <Campo rotulo="De" tipo="date" valor={de} mudar={(v) => v && setPeriodo([v, ate])} />
          <Campo rotulo="Até" tipo="date" valor={ate} mudar={(v) => v && setPeriodo([de, v])} />
        </div>
        <div className="linha" style={{ marginTop: 8 }}>
          <button className="mini" onClick={() => setPeriodo(mes(hoje()))}>Este mês</button>
          <button className="mini" onClick={() => setPeriodo(mes(hoje(), -1))}>Mês passado</button>
          <button className="mini" onClick={() => setPeriodo([`${hoje().slice(0, 4)}-01-01`, `${hoje().slice(0, 4)}-12-31`])}>Este ano</button>
        </div>
      </div>

      <div className="resultado">
        <div className="mini-dica" style={{ marginTop: 0 }}><b>Resultado</b> de {dataBR(de)} a {dataBR(ate)}</div>
        <div className="tl"><span>Lucro das vendas ({cab(res.cabVendidas)})</span><b>{dinheiro(res.lucroVendas)}</b></div>
        <div className="mini-dica" style={{ marginTop: 0 }}>Vendas {dinheiro(res.receitaVendas)} − custo médio das cabeças vendidas {dinheiro(res.custoDasVendas)}</div>
        <div className="tl clicavel" onClick={() => setVerAjuste(!verAjuste)} role="button" aria-expanded={verAjuste}>
          <span>Ajuste de contagem {verAjuste ? '▲' : '▼'}</span><b>{dinheiro(res.ajusteContagem)}</b>
        </div>
        {verAjuste && (
          <div className="mini-dica" style={{ marginTop: 0 }}>
            Cabeças encontradas {dinheiro(res.ganhoContagem)} · sumidos que apareceram {dinheiro(res.recuperadoContagem)} ·
            sumidos e zeragem − {dinheiro(res.perdaContagem)}. Pelo custo médio da categoria, na data da contagem.
          </div>
        )}
        {res.mortes > 0 && (
          <>
            <div className="tl"><span>Perda por morte: {cab(res.mortes)}, {dinheiro(res.custoMortes)} ao custo médio da data</span><b /></div>
            <div className="mini-dica" style={{ marginTop: 0 }}>
              {res.perdaMorte < res.custoMortes
                ? 'Esse custo já está espalhado nas cabeças que ficaram (o custo médio delas subiu): aparece aqui para você ver, e não se desconta de novo.'
                : ''}
              {res.perdaMorte > 0 && ` Morreu a última cabeça de uma categoria: ${dinheiro(res.perdaMorte)} ficaram sem gado para carregar e saem do resultado.`}
            </div>
            {res.perdaMorte > 0 && <div className="tl"><span>Perda por morte (sem gado para carregar o custo)</span><b>− {dinheiro(res.perdaMorte)}</b></div>}
          </>
        )}
        <div className="tl total"><span>Resultado</span><b>{dinheiro(res.resultado)}</b></div>
        <div className="tl"><span>Gastos de recria</span><b>{dinheiro(res.gastosRecria)}</b></div>
        <div className="mini-dica">A recria já está dentro do custo médio: aparece aqui para conferir, não se desconta de novo.</div>
        {variasCategorias && res.porCategoria.length > 0 && (
          <>
            <div className="mini-dica"><b>Por categoria</b> (lucro · ajuste de contagem · resultado)</div>
            {res.porCategoria.map((x) => (
              <div className="tl" key={x.categoria}>
                <span>{x.categoria}{x.cabVendidas ? ` · ${cab(x.cabVendidas)} vendidas` : ''}{x.mortes ? ` · ${num(x.mortes)} morte(s) ${dinheiro(x.custoMortes)}` : ''}</span>
                <b>{dinheiro(x.lucroVendas)} · {dinheiro(x.ajusteContagem)} · {dinheiro(x.resultado)}</b>
              </div>
            ))}
          </>
        )}
      </div>
      <div className="cartao">
        <div className="mini-dica" style={{ marginTop: 0 }}><b>Custo das entradas</b> do período — o que entrou no custo do estoque.
          Não é caixa: o pagamento de cada compra está na ficha dela.</div>
        <div className="tl"><span>Compras ({cab(res.cabEntradas)})</span><b>{dinheiro(res.custoEntradas)}</b></div>
        <div className="tl"><span>Recria</span><b>{dinheiro(res.gastosRecria)}</b></div>
        <div className="tl total"><span>Custo das entradas</span><b>{dinheiro(res.custoDasEntradas)}</b></div>
      </div>
      {pesos.length > 0 && (
        <div className="cartao">
          <div className="mini-dica" style={{ marginTop: 0 }}><b>Peso e arroba</b> — tudo o que entrou e saiu até {dataBR(ate)}</div>
          {pesos.map((p) => (
            <div key={p.categoria} style={{ padding: '4px 0' }}>
              <div><b>{p.categoria}</b></div>
              <div className="mini-dica" style={{ marginTop: 0 }}>
                {p.pesoEntrada !== null && <>Compra: {kgTexto(p.pesoEntrada)} kg/cab · </>}
                {p.custoPorArrobaEntrada !== null && <>{dinheiro(p.custoPorArrobaEntrada)}/@ · </>}
                {p.pesoVenda !== null && <>Venda: {kgTexto(p.pesoVenda)} kg/cab · </>}
                {p.ganhoPorCabeca !== null && <>ganho de peso {p.ganhoPorCabeca >= 0 ? '+' : '−'}{kgTexto(Math.abs(p.ganhoPorCabeca))} kg/cab · </>}
                {p.custoPorArrobaVendida !== null && <>custo das vendidas {dinheiro(p.custoPorArrobaVendida)}/@ · </>}
                {p.vendaPorArroba !== null && <>venda {dinheiro(p.vendaPorArroba)}/@</>}
              </div>
            </div>
          ))}
          <div className="mini-dica">Peso só quando todos os animais da categoria foram pesados; arroba só quando todos os lotes foram por arroba.</div>
        </div>
      )}
      <button className="botao fantasma" onClick={exportar}>Exportar em Excel</button>

      <h2 className="secao">Movimentos de {dataBR(de)} a {dataBR(ate)}</h2>
      {lista.length
        ? lista.map(({ m, e }) => (
          <LinhaDoMovimento key={m.id} m={m} e={e} f={fichaDe(m.fichaId)} pendente={pendentes.has(m.id)} destaque={!!fichaId && m.fichaId === fichaId}
            mostrarCategoria={variasCategorias || categoriaDoMovimento(m) !== CATEGORIA_GERAL}
            anular={gestor && !m.anulacao && !m.recusa ? () => anular(m) : undefined} ir={ir} />
        ))
        : <div className="cartao vazio">Nenhum movimento neste período.</div>}

      {form === 'recria' && <FormRecria eu={eu} cats={cats} fechar={() => setForm(null)} />}
      {form === 'morte' && <FormMorte eu={eu} cats={cats} fechar={() => setForm(null)} />}
      {form === 'contagem' && <FormContagem eu={eu} cats={cats} fechar={() => setForm(null)} />}
    </div>
  )
}

function LinhaDoMovimento({ m, e, f, pendente, destaque, mostrarCategoria = false, anular, ir }: {
  m: Movimento; e?: Efeito; f?: Ficha; pendente: boolean; destaque: boolean; mostrarCategoria?: boolean; anular?: () => void; ir: Ir
}) {
  const riscado = !!(m.anulacao || m.recusa)
  const venda = m.tipo === 'venda' || m.tipo === 'ajuste_venda'
  const categoria = categoriaDoMovimento(m)
  const rateada = categoria === RATEIO
  const cabTexto = m.tipo === 'contagem'
    ? `contou ${num(m.contadas)}${e ? ` (${e.cabDepois - e.cabAntes >= 0 ? '+' : ''}${num(e.cabDepois - e.cabAntes)})` : ''}`
    : m.cabecas ? `${m.cabecas > 0 ? '+' : ''}${num(m.cabecas)} cab` : ''
  return (
    <div className="cartao" style={{ ...(riscado ? { opacity: 0.6 } : {}), ...(destaque ? { borderColor: 'var(--verde)' } : {}) }}>
      <div className="tl">
        <span><b>{NOME_DO_TIPO[m.tipo]}</b>{rateada ? ' · rateada por cabeça' : mostrarCategoria ? ` · ${categoria}` : ''}{m.categoria ? ` · ${m.categoria}` : ''} · {dataBR(m.data)}</span>
        <b style={riscado ? { textDecoration: 'line-through' } : undefined}>{cabTexto}{m.valor ? `${cabTexto ? ' · ' : ''}${reais(m.valor)}` : ''}</b>
      </div>
      {m.descricao && <div>{m.descricao}</div>}
      {e && (
        <div className="mini-dica">
          {venda && <>Custo médio que saiu {reais(e.custoSaiu - e.custoEntrou)} · <b>lucro {reais(e.lucro)}</b> · </>}
          {e.perda > 0 && <>Sumidos {reais(e.perda)} · </>}
          {e.ganho > 0 && <>Encontradas {reais(e.ganho)} · </>}
          {e.recuperado > 0 && <>Sumidos que apareceram {reais(e.recuperado)} · </>}
          {m.tipo === 'morte' && e.custoDaMorte > 0 && <>Valiam {reais(e.custoDaMorte)} ao custo médio · </>}
          {e.perdaMorte > 0 && <>Perda por morte (sem gado para carregar o custo) {reais(e.perdaMorte)} · </>}
          {e.faltaram > 0 && <>Faltaram {cab(e.faltaram)} no registro · </>}
          {(m.kg || m.arrobas) ? <>{m.kg ? `${kgTexto(m.kg)} kg` : ''}{m.kg && m.arrobas ? ' · ' : ''}{m.arrobas ? `${num(m.arrobas, 2)} @` : ''} · </> : null}
          {rateada
            ? (e.rateio?.length
              ? <>Repartida pelas cabeças: {e.rateio.map((p) => `${p.categoria} ${reais(p.valor)} (${num(p.cabecas)} cab)`).join(' · ')}</>
              : <>Sem gado na data: entra no custo da próxima compra</>)
            : <>Fica{mostrarCategoria ? ` (${categoria})` : ''}: {cab(e.cabDepois)}{e.cabDepois ? ` · médio ${reais(e.custoDepois / e.cabDepois)}` : ''}</>}
        </div>
      )}
      <div className="mini-dica">
        lançado por {m.lancadoPorNome || 'gestor'}
        {pendente && <span className="etiqueta aberta">a enviar</span>}
        {m.anulacao && <span className="etiqueta cancelada">anulado</span>}
        {m.recusa && <span className="etiqueta cancelada">recusado pelo servidor</span>}
      </div>
      {m.anulacao && <div className="mini-dica">Motivo da anulação: {m.anulacao.motivo} · {quando(m.anulacao.em)}</div>}
      {m.recusa && <div className="mini-dica">Não entrou no Meu estoque e fica fora das contas: {m.recusa.motivo} Fica só neste aparelho.</div>}
      {(f || anular) && (
        <div className="linha" style={{ marginTop: 6 }}>
          {f && <button className="mini" onClick={() => ir({ tela: 'ficha', id: f.id, aba: 'romaneio' })}>Ver ficha {f.numero}</button>}
          {anular && <button className="mini" onClick={anular}>{m.tipo === 'entrada' || m.tipo === 'venda' ? 'Tirar do Meu estoque' : 'Anular'}</button>}
        </div>
      )}
      {/* D4: a nota da venda do gado, ou a do sal e da ração da recria */}
      {(m.tipo === 'venda' || m.tipo === 'recria') && !m.recusa && (
        <NotasLigadas ligacao={{ tipo: 'catira', id: m.id }} compacto podeAdicionar={!m.anulacao} />
      )}
    </div>
  )
}

/** «Ver no Meu estoque» de uma ficha: o que ela levou ao estoque, em qualquer data. */
function BlocoDaFicha({ f, eu, pg, movs, efeitoDe, pendentes, ir }: {
  f: Ficha; eu: Eu; pg: EstadoDePagamentos; movs: Movimento[]; efeitoDe: Map<string, Efeito>; pendentes: Set<string>; ir: Ir
}) {
  const ja = jaLancadoDaFicha(movs, f.id)
  const porCat = jaLancadoPorCategoria(movs, f.id)
  const daFicha = movs.filter((m) => m.fichaId === f.id).sort((a, b) => a.data.localeCompare(b.data) || a.em.localeCompare(b.em))
  const gestor = pode(eu.pessoa.papel, 'catira')
  const varias = (porCat?.partes.length ?? 0) > 1
  return (
    <>
      <h2 className="secao">Ficha nº {f.numero} no Meu estoque</h2>
      <div className="cartao">
        <div className="mini-dica" style={{ marginTop: 0 }}>{f.finalidade}{f.vendaDoEstoque && f.finalidade === 'Venda' ? ' do meu estoque' : ''} · {dataBR(f.data)} · {f.produtor.nome}</div>
        {ja
          ? <div className="tl"><span>{ja.tipo === 'entrada' ? 'Entrou no estoque' : 'Saiu do estoque'}</span><b>{cab(ja.cabecas)} · {reais(ja.valor)}</b></div>
          : <div>Esta ficha não está no Meu estoque.</div>}
        {varias && porCat!.partes.map((p) => (
          <div className="tl" key={p.categoria}><span>· {p.categoria}</span><b>{cab(p.cabecas)} · {reais(p.valor)}</b></div>
        ))}
        {!ja && gestor && f.situacao === 'encerrada' && (f.finalidade === 'Compra' || f.finalidade === 'Venda') && (
          <button className="botao" onClick={() => lancarFicha(f, eu, pg)}>{f.finalidade === 'Venda' ? 'Sair do meu estoque' : 'Entrar no meu estoque'}</button>
        )}
        <button className="botao fantasma pequeno" onClick={() => ir({ tela: 'ficha', id: f.id, aba: 'custo' })}>Voltar à ficha</button>
      </div>
      {daFicha.map((m) => (
        <LinhaDoMovimento key={m.id} m={m} e={efeitoDe.get(m.id)} pendente={pendentes.has(m.id)} destaque={false} mostrarCategoria ir={ir} />
      ))}
      <h2 className="secao">Estoque inteiro</h2>
    </>
  )
}

/* ---------------------------------------------------------------------------
 * Formulários — recria, morte, contagem (sem sinal também)
 * ------------------------------------------------------------------------- */

/**
 * R2D · De qual categoria é a morte ou a contagem. Com uma categoria só (ou
 * nenhuma), nem aparece: vai para ela (ou «Geral»). A lista traz o saldo de
 * cada uma, e dá para escrever uma categoria nova (gado que estava fora do
 * registro, achado na contagem).
 */
function EscolhaDeCategoria({ cats, valor, mudar, rotulo = 'Categoria' }: {
  cats: SaldoDaCategoria[]; valor: string; mudar: (v: string) => void; rotulo?: string
}) {
  if (cats.length <= 1) return null
  return (
    <Escolha rotulo={rotulo} valor={valor} mudar={mudar}
      opcoes={cats.map((x): [string, string] => [x.categoria, `${x.categoria} — ${cab(x.cabecas)}`])} />
  )
}

/** A categoria que já vem escolhida: a primeira com gado (ou a única, ou «Geral»). */
function categoriaInicial(cats: SaldoDaCategoria[]): string {
  return (cats.find((x) => x.cabecas > 0) ?? cats[0])?.categoria ?? CATEGORIA_GERAL
}

function FormRecria({ eu, cats, fechar }: { eu: Eu; cats: SaldoDaCategoria[]; fechar: () => void }) {
  const [data, setData] = useState(hoje())
  const [categoria, setCategoria] = useState('')
  // R2D: rateada pelas cabeças (o padrão) ou toda numa categoria escolhida
  const [destino, setDestino] = useState<string>(RATEIO)
  const [valor, setValor] = useState('')
  const [descricao, setDescricao] = useState('')
  const comGado = cats.filter((x) => x.cabecas > 0)
  function salvar() {
    const v = lerNumero(valor)
    if (v === null || v <= 0) { window.alert('Informe o valor gasto.'); return }
    if (!categoria) { window.alert('Escolha o tipo do gasto (pasto, sal, ração…).'); return }
    if (!data) { window.alert('Informe a data.'); return }
    lancarMovimentos(novo(eu, { tipo: 'recria', data, categoria, valor: Math.round(v * 100) / 100, descricao: descricao.trim(), categoriaGado: destino }))
    avisar('Recria guardada — vai no próximo Sincronizar')
    fechar()
  }
  return (
    <Janela titulo="+ Recria" fechar={fechar}>
      <div className="campo"><span>O que foi</span></div>
      <div className="categorias" style={{ marginBottom: 10 }}>
        {CATEGORIAS_DE_RECRIA.map((c) => (
          <button key={c} type="button" className="mini"
            style={categoria === c ? { background: 'var(--verde)', color: 'var(--verde-texto)' } : undefined}
            onClick={() => setCategoria(c)}>{c}</button>
        ))}
      </div>
      <label className="campo"><span>Valor gasto (R$)</span>
        <input inputMode="decimal" value={valor} onChange={(e) => setValor(e.target.value)} placeholder="ex.: 1.250,00" autoFocus /></label>
      <Campo rotulo="Data" tipo="date" valor={data} mudar={setData} />
      {cats.length > 1 && (
        <Escolha rotulo="Para qual gado" valor={destino} mudar={setDestino}
          opcoes={[[RATEIO, 'Todas as categorias, pelas cabeças'], ...cats.map((x): [string, string] => [x.categoria, `Só ${x.categoria} (${cab(x.cabecas)})`])]} />
      )}
      <Campo rotulo="Observação" valor={descricao} mudar={setDescricao} dica="ex.: 30 sacos de sal mineral" />
      <Dica>Soma no custo do estoque, sem mudar as cabeças. O custo médio sobe, e o lucro das próximas vendas já leva isso em conta.
        {cats.length > 1 && <> «Todas as categorias» reparte pelas cabeças de cada uma na data
          {comGado.length > 1 ? ` (hoje: ${comGado.map((x) => `${x.categoria} ${num(x.cabecas)}`).join(', ')})` : ''}. Gasto de uma categoria só
          (ex.: a ração dos bezerros): escolha a categoria, e ele vai inteiro para ela.</>}</Dica>
      <button className="botao grande" onClick={salvar}>Guardar recria</button>
      <button className="botao fantasma" onClick={fechar}>Cancelar</button>
    </Janela>
  )
}

function FormMorte({ eu, cats, fechar }: { eu: Eu; cats: SaldoDaCategoria[]; fechar: () => void }) {
  const [data, setData] = useState(hoje())
  const [qtd, setQtd] = useState('1')
  const [categoria, setCategoria] = useState(() => categoriaInicial(cats))
  const [descricao, setDescricao] = useState('')
  const saldo = estoqueAntes(lerCatira().movimentos, data || hoje(), agora(), ESTOQUE_PRINCIPAL, categoria)
  function salvar() {
    const n = Math.round(lerNumero(qtd) ?? 0)
    if (!(n > 0)) { window.alert('Informe quantas cabeças morreram.'); return }
    if (!data) { window.alert('Informe a data.'); return }
    const em = agora()
    const enc = conferirSaidas(eu, [{ categoria, sai: n }], data, em, 'a morte')
    if (enc === false) return
    lancarMovimentos(...enc, novo(eu, { tipo: 'morte', data, em, cabecas: -n, descricao: descricao.trim(), categoriaGado: categoria }))
    avisar('Morte guardada — vai no próximo Sincronizar')
    fechar()
  }
  const n = Math.round(lerNumero(qtd) ?? 0)
  return (
    <Janela titulo="Morte (animal achado)" fechar={fechar}>
      <EscolhaDeCategoria cats={cats} valor={categoria} mudar={setCategoria} />
      <label className="campo"><span>Quantas cabeças</span>
        <input inputMode="numeric" value={qtd} onChange={(e) => setQtd(e.target.value)} autoFocus /></label>
      <Campo rotulo="Data" tipo="date" valor={data} mudar={setData} />
      <Campo rotulo="Observação" valor={descricao} mudar={setDescricao} dica="ex.: picada de cobra, pasto do fundo" />
      <Dica>Tira a cabeça do estoque e o custo dela se espalha nas que ficaram (o custo médio sobe). O resultado mostra quanto a morte
        custou{saldo.custoMedio !== null && n > 0 ? ` — aqui, cerca de ${reais(saldo.custoMedio * n)}` : ''}. Animal que sumiu sem ser
        achado não é morte: é «Contagem no pasto». Estoque em {dataBR(data)}: {cab(saldo.cabecas)}.</Dica>
      <button className="botao grande" onClick={salvar}>Guardar morte</button>
      <button className="botao fantasma" onClick={fechar}>Cancelar</button>
    </Janela>
  )
}

function FormContagem({ eu, cats, fechar }: { eu: Eu; cats: SaldoDaCategoria[]; fechar: () => void }) {
  const [data, setData] = useState(hoje())
  const [contadas, setContadas] = useState('')
  const [categoria, setCategoria] = useState(() => categoriaInicial(cats))
  const [descricao, setDescricao] = useState('')
  const saldo = estoqueAntes(lerCatira().movimentos, data || hoje(), agora(), ESTOQUE_PRINCIPAL, categoria)
  const qual = cats.length > 1 ? ` de ${categoria}` : ''

  function guardar(n: number) {
    if (!data) { window.alert('Informe a data.'); return }
    const tem = saldo.cabecas
    const dif = n - tem
    const pergunta = n === 0 && tem > 0
      ? `O estoque${qual} mostra ${cab(tem)}, mas a contagem deu 0. Zerar e registrar ${num(tem)} ${tem === 1 ? 'sumido' : 'sumidos'}?\n\nSaem pelo custo médio, como ajuste de contagem (resultado, não caixa).`
      : dif < 0
        ? `O registro${qual} tem ${num(tem)}, você contou ${num(n)}. Registrar ${num(-dif)} ${dif === -1 ? 'sumido' : 'sumidos'}?\n\nSaem pelo custo médio, como ajuste de contagem (resultado, não caixa).`
        : dif > 0
          ? `O registro${qual} tem ${num(tem)}, você contou ${num(n)}. Registrar ${cab(dif)} ${dif === 1 ? 'encontrada' : 'encontradas'}?\n\nEntram pelo custo médio, como ajuste de contagem (resultado, não caixa). Se houve sumidos antes, elas os anulam primeiro.`
          : `Bateu: o registro${qual} tem ${num(tem)}. Guardar a contagem com a data?`
    if (!window.confirm(pergunta)) return
    lancarMovimentos(novo(eu, {
      tipo: 'contagem', data, contadas: n, cabecas: dif, categoriaGado: categoria,
      descricao: descricao.trim() || (n === 0 && tem > 0 ? 'Zeragem: não há mais gado no pasto' : ''),
    }))
    avisar('Contagem guardada — vai no próximo Sincronizar')
    fechar()
  }
  function salvar() {
    const n = lerNumero(contadas)
    if (n === null || n < 0 || n % 1) { window.alert('Informe quantas cabeças você contou.'); return }
    guardar(n)
  }
  return (
    <Janela titulo="Contagem no pasto" fechar={fechar}>
      <EscolhaDeCategoria cats={cats} valor={categoria} mudar={setCategoria} rotulo="Categoria contada" />
      <div className="mini-dica">O registro{qual} tem <b>{cab(saldo.cabecas)}</b> em {dataBR(data)}.{cats.length > 1 ? ' Conte uma categoria por vez.' : ''}</div>
      <label className="campo grande"><span>Quantas você contou</span>
        <input inputMode="numeric" value={contadas} onChange={(e) => setContadas(e.target.value)} placeholder="0" autoFocus /></label>
      <Campo rotulo="Data da contagem" tipo="date" valor={data} mudar={setData} />
      <Campo rotulo="Observação" valor={descricao} mudar={setDescricao} dica="ex.: contado na porteira do retiro" />
      <button className="botao grande" onClick={salvar}>Guardar contagem</button>
      {saldo.cabecas > 0 && <button className="botao fantasma" onClick={() => guardar(0)}>Não há mais gado{qual} no pasto (zerar)</button>}
      <button className="botao fantasma" onClick={fechar}>Cancelar</button>
    </Janela>
  )
}

/* ---------------------------------------------------------------------------
 * Na ficha (aba Custo): a marca «Entra no meu estoque» e o «Ver no Meu estoque»
 * ------------------------------------------------------------------------- */

/**
 * Só para o gestor, e só em ficha de Compra ou Venda. A marca não mora
 * dentro da ficha: é o movimento de entrada (ou de venda) no estoque — assim
 * só o gestor marca, o banco confere, e o comprador nem fica sabendo.
 * 3E: mora na aba Custo, e só aparece DEPOIS do encerramento (antes de entrar
 * animal não faz sentido) — ou se a ficha já está no estoque e foi reaberta.
 * R2D: a «Venda do meu estoque» sai sozinha ao encerrar; aqui só se vê (e se
 * tira, com motivo).
 */
export function CatiraNaFicha({ ficha, ir }: { ficha: Ficha; ir: Ir }) {
  const sessao = usarSessao()
  const cat = usarCatira()
  const pg = usarPagamentos()
  if (sessao.tipo !== 'dentro') return null
  const eu = sessao.eu
  const papel = eu.pessoa.papel
  if (!pode(papel, 'catira') || (ficha.finalidade !== 'Compra' && ficha.finalidade !== 'Venda')) return null
  const venda = ficha.finalidade === 'Venda'
  const ja = jaLancadoDaFicha(cat.movimentos, ficha.id)
  const porCat = jaLancadoPorCategoria(cat.movimentos, ficha.id)
  if (!ja && ficha.situacao !== 'encerrada') return null
  const rotulo = venda ? 'Sai do meu estoque' : 'Entra no meu estoque'
  const encerrada = ficha.situacao === 'encerrada'
  return (
    <>
      <h2 className="secao">Meu estoque</h2>
      <div className="cartao">
        <label className="tl" style={{ alignItems: 'center' }}>
          <span><input type="checkbox" checked={!!ja} disabled={papel !== 'gestor' || (!ja && !encerrada)}
            onChange={() => (ja ? tirarFicha(ficha.id, ficha.numero, eu) : lancarFicha(ficha, eu, pg))} /> {rotulo}</span>
          {ja && <b>{cab(ja.cabecas)} · {reais(ja.valor)}</b>}
        </label>
        {porCat && porCat.partes.length > 1 && porCat.partes.map((p) => (
          <div className="tl" key={p.categoria}><span>· {p.categoria}</span><b>{cab(p.cabecas)} · {reais(p.valor)}</b></div>
        ))}
        <div className="mini-dica">
          {ja
            ? `${venda ? 'Saiu do' : 'Entrou no'} estoque em ${dataBR(ja.principal.data)}${porCat && porCat.partes.length > 1 ? ', separado por categoria' : ''}.${papel === 'gestor' ? ' Desmarcar tira do Meu estoque, com motivo (fica riscado na lista).' : ''}`
            : papel !== 'gestor'
              ? 'Fora do Meu estoque. Só o gestor marca.'
              : !encerrada
                ? 'Encerre a ficha para marcar: o estoque recebe as cabeças e os valores do romaneio.'
                : venda
                  ? 'Marque se este gado saiu do seu estoque. As cabeças e o líquido do romaneio saem de cada categoria, e o lucro é calculado pelo custo médio dela.'
                  : porteirasDa(ficha).length
                    ? 'Marque se a porteira «Meu estoque» é sua. Entram só as cabeças e o custo dela, separados por categoria, sem comissão de cliente; cliente e revenda ficam de fora.'
                    : 'Marque só se o gado é seu. Compra para cliente não é estoque. Entra o custo total da compra, com comissão, separado por categoria.'}
        </div>
        <button className="botao fantasma pequeno" onClick={() => ir({ tela: 'catira', fichaId: ficha.id })}>Ver no Meu estoque</button>
      </div>
    </>
  )
}

/* ---------------------------------------------------------------------------
 * R2D · Venda do meu estoque — as categorias do estoque para os lotes da venda
 * ------------------------------------------------------------------------- */

/**
 * As categorias do estoque com o saldo de hoje, para os lotes da «Venda do
 * meu estoque» (Negociação e «+ Novo lote» da pesagem). Vazio: quem olha não
 * vê o estoque, ou ele não tem gado — aí valem as categorias dos Ajustes.
 */
export function usarCategoriasDoEstoque(f: Pick<Ficha, 'finalidade' | 'vendaDoEstoque'>): { categorias: string[]; rotulo: (c: string) => string } | null {
  const sessao = usarSessao()
  const cat = usarCatira()
  const calc = useMemo(() => recalcular(cat.movimentos), [cat.movimentos])
  if (f.finalidade !== 'Venda' || !f.vendaDoEstoque || sessao.tipo !== 'dentro' || !pode(sessao.eu.pessoa.papel, 'verCatira')) return null
  const cats = calc.categorias.filter((x) => x.cabecas > 0)
  if (!cats.length) return null
  return {
    categorias: cats.map((x) => x.categoria),
    rotulo: (c: string) => {
      const x = calc.categorias.find((y) => y.categoria === c)
      return x ? `${c} — ${cab(x.cabecas)} no estoque` : c
    },
  }
}
